import type { FC, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

import { RadioButton, type RadioButtonProps } from "mds/components/RadioButton";

export type RadioButtonCardProps = Omit<RadioButtonProps, "children"> & {
  children?: ReactNode;
  icon?: ReactNode;
  label: ReactNode;
  sublabel?: ReactNode;
  description?: ReactNode;
};

export const RadioButtonCard: FC<RadioButtonCardProps> = ({
  children,
  className,
  description,
  icon,
  label,
  sublabel,
  ...radioProps
}) => (
  <RadioButton
    className={twMerge(
      "cursor-pointer items-start gap-3 rounded-[12px] border border-black-tint-90 bg-white px-[18px] py-4 supports-squircle:rounded-[24px] supports-squircle:corner-squircle",
      "hover:border-black-tint-70",
      "has-[:checked]:border-blue-tint-40 has-[:checked]:bg-blue-tint-95 has-[:checked]:hover:border-blue-tint-40",
      "has-[:disabled]:cursor-not-allowed has-[:disabled]:border-black-tint-90 has-[:disabled]:bg-black-tint-97 has-[:disabled]:hover:border-black-tint-90",
      className,
    )}
    indicatorClassName="mt-0.5"
    {...radioProps}
  >
    <div className="flex min-w-0 flex-1 flex-col gap-1.5">
      <div className="flex min-w-0 items-center gap-2">
        {icon ? <span className="shrink-0 text-black-tint-40">{icon}</span> : null}
        <span className="body-s text-black-tint-20">{label}</span>
        {sublabel ? (
          <span className="body-xs flex items-center gap-2 text-black-tint-40">
            <span aria-hidden="true">·</span>
            <span>{sublabel}</span>
          </span>
        ) : null}
      </div>

      {description && <span className="body-xs text-black-tint-40">{description}</span>}

      {children && (
        <div className="mt-2 w-full" onClick={(e) => e.stopPropagation()}>
          {children}
        </div>
      )}
    </div>
  </RadioButton>
);

RadioButtonCard.displayName = "RadioButtonCard";
